import { useState } from "react";
import { Module } from "@/constants";
import { useCachedQuery } from "@/hooks/useCachedQuery";
import { useMutationWithErrorHandling } from "@/hooks/useMutationWithErrorHandling";
import { ContractorPanelPlannedHoursAccept } from "@/types/contractorPlannedHours";
import { ContractorPanelPlannedHoursService } from "../services/contractorPanelPlannedHoursService";
import { useContractorId } from "@/modules/account/hooks/useContractorId";

export const useContractorPanelPlannedHours = () => {
  const contractorId = useContractorId();
  const [acceptedPlannedHours, setAcceptedPlannedHours] =
    useState<ContractorPanelPlannedHoursAccept | null>(null);

  const statusQuery = useCachedQuery({
    queryKey: [Module.ContractorPanelPlannedHours, "status", contractorId],
    queryFn: () => ContractorPanelPlannedHoursService.getStatus(),
    staleTime: 0,
  });

  const acceptMutation = useMutationWithErrorHandling({
    mutationFn: () => ContractorPanelPlannedHoursService.accept(),
    onSuccess: (data) => {
      setAcceptedPlannedHours(data);
    },
  });

  const showHours = () => {
    acceptMutation.mutate(undefined);
  };

  const shouldShowPlannedHours = statusQuery.data?.should_show_planned_hours;
  const hasAcceptedPlannedHours = Boolean(acceptedPlannedHours);

  // Budżet bez zmian i włączona stała widoczność: status zwraca ostatnio
  // zobaczoną wartość, pokazywaną zamiast komunikatu "Budżet nie uległ zmianie".
  const lastSeenPlannedHours: ContractorPanelPlannedHoursAccept | null =
    shouldShowPlannedHours === false &&
    statusQuery.data?.period !== undefined &&
    statusQuery.data?.planned_hours !== undefined
      ? {
          period: statusQuery.data.period,
          planned_hours: statusQuery.data.planned_hours,
        }
      : null;

  const canContractorAcceptPlannedHours =
    shouldShowPlannedHours === true && !hasAcceptedPlannedHours;
  const shouldShowPlannedHoursNoChangesMessage =
    shouldShowPlannedHours === false &&
    !hasAcceptedPlannedHours &&
    !lastSeenPlannedHours;

  const isBusy =
    statusQuery.isLoading || statusQuery.isFetching || acceptMutation.isPending;

  return {
    acceptedPlannedHours: acceptedPlannedHours ?? lastSeenPlannedHours,
    isBusy,
    isSubmitting: acceptMutation.isPending,
    shouldShowPlannedHoursButton: canContractorAcceptPlannedHours,
    shouldShowNoChangesMessage: shouldShowPlannedHoursNoChangesMessage,
    showHours,
  };
};
